import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Video, Play, Clock, Calendar, Eye, Lock, Search,
  RefreshCw, ExternalLink, Filter, ChevronRight, Users, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * SESSION RECORDINGS PAGE
 * Access recordings of past live sessions
 */

const SessionRecordings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [recordings, setRecordings] = useState([]);
  const [hasFullAccess, setHasFullAccess] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [deleting, setDeleting] = useState(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('access_token');
  
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    fetchRecordings();
  }, []);

  const fetchRecordings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/session-recordings/`, { headers });
      if (res.ok) {
        const data = await res.json();
        setRecordings(data.recordings || []);
        setHasFullAccess(data.user_has_full_access || false);
        setIsAdmin(data.is_admin || false);
      }
    } catch (error) {
      console.error('Error fetching recordings:', error);
      toast.error('Failed to load recordings');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecording = async (recordingId) => {
    if (!window.confirm('Are you sure you want to delete this recording? This action cannot be undone.')) {
      return;
    }
    
    setDeleting(recordingId);
    try {
      const res = await fetch(`${API_URL}/api/session-recordings/${recordingId}`, {
        method: 'DELETE',
        headers
      });
      
      if (res.ok) {
        toast.success('Recording deleted successfully');
        setRecordings(recordings.filter(r => r.id !== recordingId));
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to delete recording');
      }
    } catch (error) {
      console.error('Error deleting recording:', error);
      toast.error('Failed to delete recording');
    } finally {
      setDeleting(null);
    }
  };

  const handleWatchRecording = async (recording) => {
    if (!recording.is_public && !hasFullAccess) {
      toast.error('This recording is only available to enrolled members');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/session-recordings/${recording.id}`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data.recording_url) {
          setSelectedRecording(data);
        } else {
          toast.error('Recording URL not available');
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Cannot access recording');
      }
    } catch (e) {
      toast.error('Error accessing recording');
    }
  };

  const filteredRecordings = recordings.filter(r => 
    r.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDuration = (minutes) => {
    if (!minutes) return 'Duration unknown';
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs > 0) {
      return `${hrs}h ${mins}m`;
    }
    return `${mins} minutes`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading session recordings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Session Recordings</h1>
              <p className="text-gray-500 mt-1">Watch recordings of past live sessions</p>
            </div>
            
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search recordings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!hasFullAccess && (
          <div className="bg-gradient-to-r from-purple-600 to-orange-500 rounded-xl p-6 text-white mb-8">
            <div className="flex items-start gap-4">
              <Lock className="w-8 h-8 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-lg mb-1">Unlock All Recordings</h3>
                <p className="text-white/80 mb-4">
                  Some recordings are only available to enrolled members. Join the academy to get full access.
                </p>
                <button
                  onClick={() => navigate('/enroll')}
                  className="bg-white text-purple-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                >
                  Enroll Now
                </button>
              </div>
            </div>
          </div>
        )}

        {filteredRecordings.length === 0 ? (
          <div className="bg-white rounded-2xl border p-12 text-center">
            <Video className="w-20 h-20 text-gray-300 mx-auto mb-6" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Recordings Available</h3>
            <p className="text-gray-500 mb-8">
              {searchTerm ? 'No recordings match your search.' : 'Check back later for session recordings.'}
            </p>
            <button
              onClick={() => navigate('/live-sessions')}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium inline-flex items-center gap-2"
            >
              <Calendar className="w-5 h-5" />
              View Upcoming Sessions
            </button>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredRecordings.map((recording) => (
              <div 
                key={recording.id} 
                className="bg-white rounded-xl border overflow-hidden hover:shadow-lg transition-shadow group"
                data-testid={`recording-${recording.id}`}
              >
                {/* Thumbnail */}
                <div className="relative aspect-video bg-gray-900">
                  {recording.thumbnail_url ? (
                    <img 
                      src={recording.thumbnail_url} 
                      alt={recording.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900 to-orange-900">
                      <Video className="w-12 h-12 text-white/50" />
                    </div>
                  )}
                  
                  {/* Play button overlay */}
                  <button
                    onClick={() => handleWatchRecording(recording)}
                    className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                      <Play className="w-8 h-8 text-purple-600 ml-1" />
                    </div>
                  </button>
                  
                  {/* Lock indicator */}
                  {!recording.is_public && !hasFullAccess && (
                    <div className="absolute top-3 right-3 bg-black/70 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Members Only
                    </div>
                  )}
                  
                  {/* Duration */}
                  <div className="absolute bottom-3 right-3 bg-black/70 text-white px-2 py-1 rounded text-xs">
                    {formatDuration(recording.duration_minutes)}
                  </div>
                </div>
                
                {/* Info */}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{recording.title}</h3>
                  {recording.description && (
                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">{recording.description}</p>
                  )}
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {recording.recorded_at ? new Date(recording.recorded_at).toLocaleDateString('en-GB') : 'Unknown'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {recording.views || 0} views
                    </span>
                  </div>
                  
                  {/* Delete button for admins */}
                  {isAdmin && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteRecording(recording.id);
                      }}
                      disabled={deleting === recording.id}
                      className="mt-3 w-full px-3 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                    >
                      {deleting === recording.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                      {deleting === recording.id ? 'Deleting...' : 'Delete Recording'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Video Player Modal */}
      {selectedRecording && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-4xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-white text-xl font-bold">{selectedRecording.title}</h2>
              <button 
                onClick={() => setSelectedRecording(null)}
                className="text-white hover:text-gray-300 text-2xl"
              >
                ×
              </button>
            </div>
            
            {selectedRecording.recording_url ? (
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                {/* YouTube embed */}
                {(selectedRecording.recording_url.includes('youtube') || selectedRecording.recording_url.includes('youtu.be')) ? (
                  <iframe
                    src={selectedRecording.recording_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                    className="w-full h-full"
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  />
                ) : selectedRecording.recording_url.includes('vimeo') ? (
                  /* Vimeo embed */
                  <iframe
                    src={selectedRecording.recording_url.replace('vimeo.com/', 'player.vimeo.com/video/')}
                    className="w-full h-full"
                    allowFullScreen
                  />
                ) : selectedRecording.recording_url.includes('zoom.us') ? (
                  /* Zoom Recording - Cannot be embedded, show play button */
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-blue-900 to-blue-700">
                    <div className="text-center">
                      <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                        <svg className="w-16 h-16 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M4 4.5V19.5C4 20.88 5.12 22 6.5 22H17.5C18.88 22 20 20.88 20 19.5V6.5L14 0.5H6.5C5.12 0.5 4 1.62 4 3V4.5ZM9 12V17H7V12H9ZM13 9V17H11V9H13ZM17 14V17H15V14H17Z"/>
                        </svg>
                      </div>
                      <h3 className="text-white text-xl font-semibold mb-2">Zoom Recording</h3>
                      <p className="text-blue-200 mb-6 max-w-md">
                        This recording is hosted on Zoom and will open in a new window for the best viewing experience.
                      </p>
                      <a
                        href={selectedRecording.recording_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:bg-blue-50 transition-colors text-lg shadow-lg"
                      >
                        <Play className="w-6 h-6" />
                        Watch on Zoom
                      </a>
                    </div>
                  </div>
                ) : selectedRecording.recording_url.includes('drive.google.com') ? (
                  /* Google Drive Video embed */
                  <iframe
                    src={selectedRecording.recording_url.replace('/view', '/preview').replace('?usp=sharing', '')}
                    className="w-full h-full"
                    allowFullScreen
                    allow="autoplay"
                  />
                ) : (
                  /* Default: Direct video file */
                  <video 
                    src={selectedRecording.recording_url} 
                    controls 
                    className="w-full h-full"
                    autoPlay
                  />
                )}
              </div>
            ) : (
              <div className="aspect-video bg-gray-800 rounded-lg flex items-center justify-center">
                <p className="text-gray-400">Recording not available</p>
              </div>
            )}
            
            <div className="mt-4 flex justify-between items-center">
              <p className="text-gray-400 text-sm">{formatDuration(selectedRecording.duration_minutes)}</p>
              {selectedRecording.recording_url && !selectedRecording.recording_url.includes('zoom.us') && (
                <a
                  href={selectedRecording.recording_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-400 hover:text-purple-300 flex items-center gap-1 text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open in new tab
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionRecordings;
